import { App } from '@capacitor/app';
import { useRouter } from 'vue-router';
import { useCapacitor } from './useCapacitor';

/**
 * Composable for deep linking (Universal Links / App Links)
 */
export function useDeepLinking() {
  const router = useRouter();
  const { isNative } = useCapacitor();

  /**
   * Parse deep link URL and extract route information
   */
  function parseDeepLink(url: string): { route: string; params?: Record<string, string> } | null {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      
      // Parse common routes:
      // /post/:ulid -> /post/:ulid
      // /profile/:username -> /profile/:username
      // /playlist/:id -> /playlist/:id
      
      if (path.startsWith('/post/')) {
        const ulid = path.split('/post/')[1]?.split('/')[0];
        if (ulid) return { route: `/post/${ulid}` };
      }

      if (path.startsWith('/v/') || path.startsWith('/s/')) {
        const ulid = path.replace(/^\/[vs]\//, '').split('/')[0];
        if (ulid) return { route: `/post/${ulid}` };
      }

      if (path.startsWith('/profile/')) {
        const username = path.split('/').pop();
        return { route: `/profile/${username}` };
      }
      
      if (path.startsWith('/playlist/')) {
        const parts = path.split('/playlist/')[1].split('/');
        const id = parts[0];
        const slug = parts[1];
        return { route: `/playlist/${id}${slug ? `/${slug}` : ''}` };
      }
      
      // Default: use path as route
      return { route: path || '/' };
    } catch (error) {
      console.error('[DeepLinking] Error parsing URL:', error);
      return null;
    }
  }

  /**
   * Handle deep link
   */
  function handleDeepLink(url: string): void {
    const parsed = parseDeepLink(url);
    if (parsed) {
      router.push(parsed.route);
    } else {
      router.push('/');
    }
  }

  /**
   * Initialize deep linking listeners
   */
  function initialize(): () => void {
    if (!isNative) {
      return () => {}; // No-op cleanup
    }

    // Listen for app URL open events (deep links)
    App.addListener('appUrlOpen', (event) => {
      console.log('[DeepLinking] App opened via URL:', event.url);
      handleDeepLink(event.url);
    }).then((listener) => {
      // Store listener for cleanup if needed
      return listener;
    });

    // Return cleanup function (simplified for now)
    return () => {}; // Cleanup handled by Capacitor
  }

  return {
    initialize,
    handleDeepLink,
    parseDeepLink,
    isSupported: isNative
  };
}
